import { useContext, useCallback, useEffect, useRef } from 'react'
import debounce from 'lodash/debounce'
import { useRouteMatch } from 'react-router-dom'
import { SubmissionsDetailsHeader } from '@components/headers/submissionsDetails'
import { Status, headerTitleIsInvalid } from '@components/layout/headerNav'
import { SelectiveDatasetContext } from '@datasets/create-dataset/selective/context'
import { useAppSelector } from '@redux/hooks'

/**
 * Separate logic for the selective editing portion of the submissions header.
 */
interface SubmissionsHeaderSelectiveContainerRouterParams {
  id?: string
}

export const SubmissionsHeaderSelectiveContainer = () => {
  const isSubmissionPage =
    useRouteMatch<SubmissionsHeaderSelectiveContainerRouterParams>('/codes/details/:id')
  const id = isSubmissionPage?.params.id ?? null
  const meta = useAppSelector(state => state.Datasets.datasetMetadataById[id])
  const submissionTemplate = useAppSelector(
    state => state.Datasets.submissionTemplatesById[meta?.template_id],
  )
  const { state, dispatch: dispatchState } = useContext(SelectiveDatasetContext)
  const { submissionName } = state
  const userEditingSubmissionName = useRef(false)
  const isUserEditingSubmissionName = userEditingSubmissionName.current
  const isHeaderTitleInvalid = headerTitleIsInvalid({
    isRequired: submissionTemplate?.settings?.require_identifier,
    submissionName,
  })

  const debouncedEditableTitle = debounce(function debouncedEditableTitle(value) {
    userEditingSubmissionName.current = true
    dispatchState({ type: 'setSubmissionName', payload: value })
  }, 300)

  const onBlurCallback = useCallback(_value => {
    dispatchState({ type: 'setUnblockSubmit' })
  }, [])

  const onFocusCallback = useCallback(function onFocusCallback() {
    dispatchState({ type: 'setBlockSubmit' })
  }, [])

  // Set submission name based on dataset.
  // - Editing from an existing submission, use submission name.
  // - Do not set the submission name from dataset if the user starts editing the title.
  // -- It was rendering this in the order of debouncedEditableTitle change, this useEffect, then a render;
  // -- causing the dataset initial name to stomp on top of the user change when it re-rendered.
  useEffect(() => {
    if (!meta?.identifier) return
    if (isUserEditingSubmissionName) return
    if (submissionName === meta?.identifier) return

    dispatchState({ type: 'setSubmissionName', payload: meta?.identifier })
  }, [meta?.identifier, submissionName])

  return (
    <SubmissionsDetailsHeader
      title={submissionName}
      status={Status.UPDATE_ROWS}
      editableTitleCallback={debouncedEditableTitle}
      editableTitleRequired={submissionTemplate?.settings?.require_identifier}
      titleValidationError={isHeaderTitleInvalid}
      onBlurCallback={onBlurCallback}
      onFocusCallback={onFocusCallback}
    />
  )
}
